import classNames from 'classnames';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { TwTheme } from '../../../Tw';
import useGetNamedColor from '../../common/useGetNamedColor';

const colors = TwTheme.extend.colors;

interface ChartSidebarRowProps {
  id: string;
  label: string;
  labelHelper?: string;
  color?: [string, string];
  isSelected?: boolean;
  onClick?: (id: string) => void;
  onHover?: (id: string) => void;
  height?: string;
  showColorIndicator?: boolean;
}

interface ChartSidebarProps {
  rows: ChartSidebarRowProps[];
  position?: 'left' | 'right';
  sticky?: boolean;
  absolute?: boolean;
  width?: string;
  topOffset?: string;
  className?: string;
  showHeader?: boolean;
  headerHeight?: string;
  headerTitle?: string;
  rowsHeight?: string;
}

const ChartSidebarRow: React.FC<ChartSidebarRowProps> = ({
  id,
  label,
  labelHelper,
  color,
  isSelected = false,
  onClick,
  onHover,
  height = 'auto',
  showColorIndicator = true,
}) => {
  const handleClick = () => onClick?.(id);
  const handleMouseEnter = () => onHover?.(id);

  return (
    <div
      data-component="ChartSidebarRow"
      className={classNames(
        'w-full',
        'h-full',
        'flex-1',
        'flex items-center space-y-px',
        'pl-1 pr-3',
        'cursor-pointer transition-colors duration-150',
        'hover:bg-brand/10 active:bg-brand/10',
        'text-end',
        'border-app-panel-dark/40 border-b',
        {
          'bg-brand/10 text-menu-active-text': isSelected,
          'bg-menu/10': !isSelected,
        },
        'truncate',
        // 'mb-[4px] mt-[1px]',
      )}
      // style={{ height }}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      title={label}
      style={{
        height: height ? `calc(${height} - 3px)` : undefined,
        minHeight: height ? `calc(${height} - 3px)` : undefined,
      }}
    >
      {/* Color indicator */}
      {/* {showColorIndicator && color && (
        <div
          className="mr-2 h-3 w-3 flex-shrink-0 rounded-sm"
          style={{ backgroundColor: color[0] }}
        />
      )} */}

      {labelHelper && (
        <span className="text-minimal bg-app-panel/50 backdrop-saturate-110 backdrop-filte left-0 rounded p-1 opacity-0 backdrop-blur-lg transition-all duration-150 group-hover:opacity-100 ltr:mr-2 rtl:ml-2">
          {labelHelper}
        </span>
      )}
      {/* Label */}
      <span
        className={classNames('flex-1 truncate', {
          'text-menu-active-text': isSelected,
          'text-menu-text': !isSelected,
        })}
      >
        {label}
      </span>
    </div>
  );
};

const ChartSidebar: React.FC<ChartSidebarProps> = ({
  rows,
  position = 'left',
  sticky = false,
  absolute = false,
  width = '150px',
  topOffset = '0px',
  className,
  showHeader = false,
  // headerHeight = '64px',
  headerTitle = 'Groups',
  rowsHeight,
}) => {
  const { t } = useTranslation('feed');
  const getNamedColor = useGetNamedColor();

  const stickyClasses = sticky
    ? `sticky top-0 ${position === 'left' ? 'left-0' : 'right-0'}`
    : '';

  const absoluteClasses = absolute
    ? `absolute top-0 bottom-0 ${position === 'left' ? 'left-0' : 'right-0'}`
    : '';

  return (
    <div
      data-component="ChartSidebar"
      className={classNames(
        'group',
        'bg-menu/10',
        'z-10',
        'flex flex-col items-center',
        'backdrop-saturate-110 backdrop-blur-lg backdrop-filter',
        'h-full w-full',

        // 'w-[150px]',
        'text-menu-text',
        'text-xs',
        stickyClasses,
        absoluteClasses,
        className,
      )}
      style={{
        width,
        top: topOffset,
        maxHeight: sticky ? `calc(100vh - ${topOffset})` : '100vh',
      }}
    >
      {/* Header */}
      {showHeader && (
        <div
          className="flex items-center justify-center"
          // style={{ height: headerHeight }}
        >
          <span className="text-sm font-medium uppercase tracking-wide">
            {headerTitle}
          </span>
        </div>
      )}

      {/* Scrollable content */}
      <div
        data-component="ChartSidebarContent"
        className="flex h-full w-full flex-1 flex-col overflow-hidden"
      >
        {_.map(rows, (row, index) => {
          // Get color for the row if not provided
          const rowColor =
            row.color ||
            (_.head(getNamedColor(row.id)) as unknown as [string, string]);

          const labelHelper = `${index + 1}`;
          return (
            <ChartSidebarRow
              key={row.id}
              {...row}
              labelHelper={labelHelper}
              color={rowColor}
              height={rowsHeight}
            />
          );
        })}
      </div>

      {/* Empty state */}
      {_.isEmpty(rows) && (
        <div className="flex items-center justify-center p-4 text-sm">
          {t`No data available`}
        </div>
      )}
    </div>
  );
};

export default ChartSidebar;
